import { AlertTriangle, CircleCheck } from "lucide-react";
import type { ReactNode } from "react";

import type { Scope } from "@busright/data-contract";

import { Drillable } from "@/components/Drillable";
import { MetricLabel } from "@/components/MetricTooltip";
import { formatUsdCompact } from "@/lib/format";

// =================================================================================================
// FinLatePaymentContext — the credit-hold banner. Reads the FinanceMoMPayload's late-payment +
// credit-posture fields and states, in plain language, whether BusRight is currently on (or close
// to) a credit hold. Late-payment total is Drillable into its own metric so a reader can see WHICH
// invoices are late.
// =================================================================================================

export interface FinLatePaymentContextProps {
  readonly totalLatePaymentUsd: number;
  readonly remainingCreditLimitUsd: number;
  readonly onCreditHold: boolean;
  readonly scope: Scope;
}

export const FinLatePaymentContext = ({
  totalLatePaymentUsd,
  remainingCreditLimitUsd,
  onCreditHold,
  scope,
}: FinLatePaymentContextProps): ReactNode => (
  <div className={`fin-credit-hold-banner ${onCreditHold ? "hold" : "clear"}`}>
    {onCreditHold ? (
      <AlertTriangle size={16} aria-hidden style={{ flexShrink: 0 }} />
    ) : (
      <CircleCheck size={16} aria-hidden style={{ flexShrink: 0 }} />
    )}
    <div>
      <p>
        {onCreditHold
          ? "BusRight is currently ON credit hold — remaining credit is at or below zero."
          : "BusRight is NOT on credit hold — remaining credit is positive."}
      </p>
      <div className="metric-strip" style={{ marginTop: 8 }}>
        <Drillable
          metricKey="finance.latePaymentUsd"
          scope={scope}
          label="Drill into late payments — which invoices are past due"
        >
          <div className="metric-chip">
            <span className="mc-label">
              <MetricLabel metricKey="finance.latePaymentUsd" label="Late payments" />
            </span>
            <span className="mc-value br-tabular-nums">
              {formatUsdCompact(totalLatePaymentUsd)}
            </span>
          </div>
        </Drillable>
        <Drillable
          metricKey="finance.creditRemainingUsd"
          scope={scope}
          label="Drill into remaining credit"
        >
          <div className="metric-chip">
            <span className="mc-label">
              <MetricLabel metricKey="finance.creditRemainingUsd" label="Credit remaining" />
            </span>
            <span className="mc-value br-tabular-nums">
              {formatUsdCompact(remainingCreditLimitUsd)}
            </span>
          </div>
        </Drillable>
      </div>
    </div>
  </div>
);
